import type { AxiosRequestConfig } from 'axios'
import type { ApiListPayload } from '@/types/api.types'
import {
  apiDelete,
  apiDeleteResult,
  apiDownloadBlob,
  apiGet,
  apiPatchResult,
  apiPostResult,
  triggerBlobDownload,
} from '@/services/api/http'
import type { SalesLeadStatus } from '@/modules/sales/constants/lead-status'
import type { SystemRole } from '@/types/permission.types'
import { appendCsvParam } from '@/utils/date-filters'

export interface SalesLeadUserRef {
  id: string
  firstName: string
  lastName: string
  email: string
  role?: SystemRole
  /** Present on the assignees list only — the assignee's own Sales Territory
   * membership (null = unrestricted / non-territory user). */
  salesTerritoryId?: string | null
  salesTerritoryCode?: string | null
  salesManagerId?: string | null
}

export interface SalesLeadRemark {
  id: string
  body: string
  authorId: string
  author: SalesLeadUserRef
  createdAt: string
}

export type SalesLeadContactChannel = 'call' | 'whatsapp' | 'visit' | 'other'
export type SalesLeadContactOutcome =
  | 'connected'
  | 'no_answer'
  | 'busy'
  | 'wrong_number'
  | 'voicemail'
  | 'other'

export interface SalesLeadContact {
  id: string
  channel: SalesLeadContactChannel
  outcome: SalesLeadContactOutcome
  note: string | null
  nextFollowUpAt: string | null
  /** Written by a tracked call (null = legacy / manual log). */
  callSessionId?: string | null
  callbackAt?: string | null
  authorId: string
  author: SalesLeadUserRef
  createdAt: string
}

export interface SalesLeadProductLine {
  id: string
  description: string
  quantity: number | null
  /** Optional master-data category on the raw Query line. */
  categoryId: string | null
  categoryNameSnapshot: string | null
  sortOrder: number
}

/** Free-text raw product line for create/update — description required when kept;
 * category is optional master-data link (not catalog design/quality/color). */
export interface SalesLeadProductLineInput {
  description: string
  quantity?: number | null
  categoryId?: string | null
}

export type RegisteredLeadStatus = 'open' | 'deal_done'

export interface RegisteredLeadItem {
  id: string
  productId: string
  productNameSnapshot: string
  categoryId: string
  categoryNameSnapshot: string | null
  qualityId: string | null
  qualityNameSnapshot: string | null
  colorId: string | null
  colorNameSnapshot: string | null
  quantity: number | null
  quotePrice: number | null
  sortOrder: number
}

/** Lean shape carried on list rows — rlNo + status + a one-line summary only. */
export interface RegisteredLeadSummary {
  id?: string
  rlNo: string
  registeredStatus: RegisteredLeadStatus
  itemCount: number
  firstProductName: string | null
  /** Sum of registered line quantities; null when no quantities set. */
  totalQuantity?: number | null
}

/** Full shape — only on GET /sales-leads/:id. Identity fields are the RL snapshot
 * (independent of Query); assignee/follow-up remain on the parent Query. */
export interface RegisteredLead {
  id: string
  /** Server-generated, e.g. "RL-1" — never client-settable. */
  rlNo: string
  registeredStatus: RegisteredLeadStatus
  businessName: string
  name: string
  number: string
  normalizedPhone: string | null
  businessType: string | null
  email: string | null
  cityId: string | null
  cityNameSnapshot: string | null
  regionSnapshot: string | null
  salesTerritoryId: string | null
  salesTerritoryCodeSnapshot: string | null
  salesTerritoryNameSnapshot: string | null
  leadSource: string | null
  closingNote: string | null
  dealValue: number | null
  dealReference: string | null
  items: RegisteredLeadItem[]
  createdById: string
  createdBy: SalesLeadUserRef
  updatedById: string
  updatedBy: SalesLeadUserRef
  createdAt: string
  updatedAt: string
  /** At most one selected quotation per lead — null when none selected. */
  selectedQuotationId: string | null
  /** Live summary of the selected quote (not a snapshot) — read fresh from
   * Quotation each time, unlike everything else on Quotation itself. */
  selectedQuotation: RegisteredLeadSelectedQuotation | null
}

export interface RegisteredLeadSelectedQuotation {
  id: string
  quoteNo: string
  status: 'draft' | 'sent' | 'accepted' | 'rejected'
  totalAmount: number
  quoteDate: string
}

/** Lean row shape returned by GET /sales-leads (list) — full contact/remark
 * history and product tables still live only on the detail payload;
 * lastRemark below is the single most-recent remark. */
export interface SalesLeadListItem {
  id: string
  /** Server-generated, e.g. "Q-1" — never client-settable. */
  leadNo: string
  businessName: string
  name: string
  number: string
  cityNameSnapshot: string | null
  regionSnapshot: string | null
  /** Sales Territory (business geography) — never the same concept as
   * regionSnapshot (province). Null = unmapped city, left null on purpose. */
  salesTerritoryId: string | null
  salesTerritoryCodeSnapshot: string | null
  /** Already carries the code, e.g. "Central Punjab (R2)" / "Open". */
  salesTerritoryNameSnapshot: string | null
  leadSource: string | null
  /** Orders only in a named season (lemon, mango…). */
  seasonal?: boolean
  seasonLabel?: string | null
  status: SalesLeadStatus
  /** Why it moved to Proceed. */
  proceedReason?: string | null
  /** In Progress with no contact for 7 days. */
  needsResult?: boolean
  /** Historical lead closed (old "Deal Done") before registration existed — status
   * reads as registered with no RegisteredLead attached. */
  grandfathered: boolean
  /** Only set when status === 'cancelled'. */
  cancelReason?: string | null
  /** Most recent remark's body, if any — full history is detail-only. */
  lastRemark?: string | null
  nextFollowUpAt: string | null
  /** Countdown to nextFollowUpAt in Asia/Karachi calendar days — negative when
   * overdue, null when there's no pending follow-up. Never recompute client-side. */
  remainingDays: number | null
  /** Calls: callback date + time (UTC ISO); nextFollowUpAt is its date-only mirror. */
  callbackAt?: string | null
  lastCallAt?: string | null
  /** Set by a "Wrong number" call result; cleared when the number changes. */
  numberFlaggedAt?: string | null
  /** Customer asked not to be called. */
  doNotCall?: boolean
  assignedUserId: string
  assignedUser: SalesLeadUserRef
  /** Sheet names when this lead came from a combined old customer, e.g. "Shabbir / Haleema". */
  agentLabel?: string | null
  createdById: string
  createdBy: SalesLeadUserRef
  updatedById: string
  updatedBy: SalesLeadUserRef
  registeredLead: RegisteredLeadSummary | null
  /** First Query free-text product line description (not RL product name). */
  firstProductDescription?: string | null
  /** First Query line category name when set (optional master-data link). */
  firstCategoryName?: string | null
  /** Count of Query product lines. */
  productLineCount?: number
  /** Sum of query product-line required quantities; null when none set. */
  totalQuantity?: number | null
  createdAt: string
  updatedAt: string
}

/** Registered Leads tab row — RL snapshot identity + item summary.
 * leadId/leadNo are Query cross-refs; assignee/follow-up come from the Query. */
export interface RegisteredLeadListItem {
  id: string
  leadId: string
  leadNo: string
  rlNo: string
  /** Quotations raised for this registered lead (list DTO). */
  quotationCount?: number
  businessName: string
  name: string
  number: string
  cityId?: string | null
  cityNameSnapshot?: string | null
  regionSnapshot?: string | null
  salesTerritoryId?: string | null
  salesTerritoryCodeSnapshot?: string | null
  salesTerritoryNameSnapshot?: string | null
  leadSource?: string | null
  businessType?: string | null
  email?: string | null
  status: RegisteredLeadStatus
  assignedUserId: string
  assignedUser: SalesLeadUserRef
  agentLabel?: string | null
  nextFollowUpAt?: string | null
  remainingDays: number | null
  /** From the parent Query — a registered lead is never itself cancelled,
   * but the field is here for shape parity with SalesLeadListItem. */
  cancelReason?: string | null
  /** Most recent remark's body, from the parent Query. */
  lastRemark?: string | null
  itemCount: number
  firstProductName: string | null
  firstCategoryName?: string | null
  firstQualityName?: string | null
  firstColorName?: string | null
  /** First registered line quantity (aligned with category / color / quote). */
  firstQuantity?: number | null
  firstQuotePrice?: number | null
  /** Sum of registered line quantities; null when no quantities set. */
  totalQuantity?: number | null
  /** Σ (qty × quotePrice) across every line — server-computed, same math as
   * quotedLinesTotal() in lead-format.ts, never re-derived client-side. */
  totalPrice?: number
  createdById?: string
  createdBy?: SalesLeadUserRef
  updatedById?: string
  updatedBy?: SalesLeadUserRef
  /** Parent query createdAt — same Date column as the Queries tab. */
  createdAt: string
  updatedAt: string
}

/** Full detail shape — GET /sales-leads/:id and every write response. */
export interface SalesLead extends Omit<SalesLeadListItem, 'registeredLead'> {
  cityId: string | null
  normalizedPhone: string | null
  businessType: string | null
  email: string | null
  cancelReason: string | null
  remarks: SalesLeadRemark[]
  contacts: SalesLeadContact[]
  productLines: SalesLeadProductLine[]
  registeredLead: RegisteredLead | null
}

/** Non-blocking — a matching phone on another lead, surfaced for the actor to see. */
export interface DuplicatePhoneWarning {
  id: string
  leadNo: string
  businessName: string
  name: string
  status: SalesLeadStatus
  assignedUser: SalesLeadUserRef
}

export interface SalesLeadWriteResult {
  lead: SalesLead
  duplicateWarning: DuplicatePhoneWarning | null
}

export type SalesLeadPeriod = 'today' | 'week' | 'month' | 'year' | 'all'
/** lead (default): every lead, including registered ones. registered: only leads with a RegisteredLead. */
export type SalesLeadStage = 'lead' | 'registered'

/** Server-side sort key (Users `sortBy`/`sortDir` pattern) — see
 * `salesLeadService.js#resolveLeadOrderBy`. Query/Follow-up and Registered
 * each use their own subset; an unrecognized key falls back to the default
 * (`createdAt` desc) server-side, never a 400/500. */
export type SalesLeadSortBy =
  | 'createdAt'
  | 'businessName'
  | 'name'
  | 'cityName'
  | 'status'
  | 'nextFollowUpAt'
  | 'totalQuantity'
  | 'product'
  | 'assignedTo'
  | 'leadNo'
  | 'email'
  | 'registeredStatus'
  | 'rlNo'
  /** Registered-only (Σ qty × quotePrice) — in-memory sort on the backend,
   * same pattern as totalQuantity/product. */
  | 'totalPrice'
export type SalesLeadSortDir = 'asc' | 'desc'

export type SalesLeadExportFormat = 'xlsx' | 'pdf'
/** Which list tab this export was triggered from — filename/title labeling
 * only (see salesLeadService.js#exportRows on the backend); the actual row
 * filtering is unchanged from whatever `SalesLeadListParams` already sends
 * (stage + status=follow_up for the Follow tab, exactly like the list). */
export type SalesLeadExportTab = 'lead' | 'follow' | 'registered'
/** XLSX only — which sheet shape to build; the backend ignores this for PDF
 * (always lead-level). `by_line`: one row per product line, parent fields
 * repeated. `summary`: one row per lead, same "+N more" contract as the
 * on-screen list. Backend defaults to `by_line` when omitted. */
export type SalesLeadExportLayout = 'by_line' | 'summary'

export interface SalesLeadListParams {
  search?: string
  stage?: SalesLeadStage
  /** Legacy singular — merged server-side with assignedUserIds. */
  assignedUserId?: string
  assignedUserIds?: string[]
  status?: SalesLeadStatus | RegisteredLeadStatus
  cityId?: string
  cityIds?: string[]
  region?: string
  salesTerritoryId?: string
  salesTerritoryCode?: string
  leadSource?: string
  leadSources?: string[]
  /** createdAt window priority (server-side): dateFrom/dateTo > months > period.
   * Omit all three (or period=all) for all time. */
  period?: SalesLeadPeriod
  months?: string[]
  dateFrom?: string
  dateTo?: string
  /** Follow-up tab: filter by nextFollowUpAt (`today` | `overdue`). */
  followUpDue?: 'today' | 'overdue'
  /** Total quantity on the lead. Either side may be omitted. */
  qtyFrom?: number
  qtyTo?: number
  /** Registered list: has a quote, or has none. */
  quotes?: 'has' | 'none'
  /** Excel column contains filters */
  queryRef?: string
  businessName?: string
  name?: string
  number?: string
  email?: string
  businessType?: string
  /** true = seasonal only. */
  seasonal?: boolean
  product?: string
  category?: string
  quality?: string
  color?: string
  createdBy?: string
  updatedBy?: string
  sortBy?: SalesLeadSortBy
  sortDir?: SalesLeadSortDir
  page?: number
  pageSize?: number
}

export interface SalesLeadSummaryAssignee {
  userId: string
  firstName: string
  lastName: string
  count: number
}

/** Full filtered set (same where as the list) — not scoped to the current page. */
export interface SalesLeadSummary {
  total: number
  byAssignee: SalesLeadSummaryAssignee[]
  /** Always all five keys, 0 when a status has no matching leads (never a partial object). */
  byStatus: Record<SalesLeadStatus, number>
  leadCount: number
  registeredCount: number
  /** Queries with source Outbound inside the same filter window. */
  outboundCount?: number
}

export interface PaginatedSalesLeads {
  data: SalesLeadListItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
  summary: SalesLeadSummary
}

export interface PaginatedRegisteredLeads {
  data: RegisteredLeadListItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
  summary: SalesLeadSummary
}

export interface SalesLeadCreateInput {
  businessName: string
  name: string
  number: string
  cityId: string
  leadSource: string
  /** Required unless the actor is a sales agent without Assign (self-assigned). */
  assignedUserId?: string
  /** Explicit override only — resolved server-side from cityId by default;
   * honored only for an actor holding sales_leads.assign (Q3). */
  salesTerritoryId?: string
  businessType?: string | null
  email?: string | null
  seasonal?: boolean
  seasonLabel?: string | null
  remark?: string
  /** Free-text raw product lines — optional categoryId; no catalog design/quality/color. */
  productLines?: SalesLeadProductLineInput[]
}

export type SalesLeadUpdateInput = Partial<{
  proceedReason: string
  proceedRemark: string
  businessName: string
  name: string
  number: string
  cityId: string
  leadSource: string
  assignedUserId: string
  /** Explicit override only — see SalesLeadCreateInput. */
  salesTerritoryId: string
  businessType: string | null
  email: string | null
  seasonal: boolean
  seasonLabel: string | null
  /** Replace-all semantics when present. */
  productLines: SalesLeadProductLineInput[]
  /** 'new' is create-only and 'registered' is reachable only via Register.
   * Unregister = status in_progress (deletes RegisteredLead). Other pipeline
   * targets: follow_up / cancelled (not from registered). */
  status: 'in_progress' | 'follow_up' | 'seasonal' | 'proceed' | 'cancelled'
  cancelReason: string
  /** ISO timestamp, or null to clear. */
  nextFollowUpAt: string | null
  /** Customer asked not to be called (Calls). */
  doNotCall: boolean
}>

export interface RegisteredLeadItemInput {
  productId: string
  qualityId?: string | null
  colorId?: string | null
  quantity?: number | null
  quotePrice?: number | null
}

/** Register / updateRegistered — RL identity snapshot + items.
 * Query fields are never rewritten by these endpoints. */
export type SalesLeadRegisterInput = {
  businessName: string
  name: string
  number: string
  cityId: string
  leadSource: string
  businessType?: string | null
  email?: string | null
  salesTerritoryId?: string | null
  items: RegisteredLeadItemInput[]
}

export type SalesLeadUpdateRegisteredInput = SalesLeadRegisterInput

export interface SalesLeadContactInput {
  channel: SalesLeadContactChannel
  outcome: SalesLeadContactOutcome
  note?: string
  nextFollowUpAt?: string | null
  /** Optional — decided when logging contact / moving status after a call. */
  seasonal?: boolean
}

/** Deal Done — acts on the registered record, not the parent lead. */
export interface SalesLeadCloseInput {
  closingNote: string
  dealValue?: number | null
  dealReference?: string
}

function toListQuery(params: SalesLeadListParams): Record<string, string | number> {
  const query: Record<string, string | number> = {}

  const search = params.search?.trim()
  if (search) query.search = search
  if (params.stage) query.stage = params.stage
  if (params.assignedUserId) query.assignedUserId = params.assignedUserId
  appendCsvParam(query, 'assignedUserIds', params.assignedUserIds)
  if (params.status) query.status = params.status
  if (params.cityId) query.cityId = params.cityId
  appendCsvParam(query, 'cityIds', params.cityIds)
  if (params.region?.trim()) query.region = params.region.trim()
  if (params.salesTerritoryId) query.salesTerritoryId = params.salesTerritoryId
  if (params.salesTerritoryCode) query.salesTerritoryCode = params.salesTerritoryCode
  if (params.leadSource?.trim()) query.leadSource = params.leadSource.trim()
  appendCsvParam(query, 'leadSources', params.leadSources)

  // Same priority as the server: dateFrom/dateTo > months > period. Never send more
  // than one of the three — the caller is responsible for clearing the others.
  const dateFrom = params.dateFrom?.trim()
  const dateTo = params.dateTo?.trim()
  if (dateFrom || dateTo) {
    if (dateFrom) query.dateFrom = dateFrom
    if (dateTo) query.dateTo = dateTo
  } else if (params.months?.length) {
    appendCsvParam(query, 'months', params.months)
  } else if (params.period && params.period !== 'all') {
    query.period = params.period
  }
  if (params.followUpDue === 'today' || params.followUpDue === 'overdue') {
    query.followUpDue = params.followUpDue
  }
  if (params.qtyFrom != null) query.qtyFrom = params.qtyFrom
  if (params.qtyTo != null) query.qtyTo = params.qtyTo
  if (params.quotes === 'has' || params.quotes === 'none') query.quotes = params.quotes
  if (params.seasonal === true) query.seasonal = 'true'

  const textKeys = [
    'queryRef',
    'businessName',
    'name',
    'number',
    'email',
    'businessType',
    'product',
    'category',
    'quality',
    'color',
    'createdBy',
    'updatedBy',
  ] as const
  for (const key of textKeys) {
    const value = params[key]?.trim()
    if (value) query[key] = value
  }

  // Defaults match the backend's — omit so the request/cache key stays clean.
  if (params.sortBy && params.sortBy !== 'createdAt') query.sortBy = params.sortBy
  if (params.sortDir && params.sortDir !== 'desc') query.sortDir = params.sortDir

  if (params.page != null) query.page = params.page
  if (params.pageSize != null) query.pageSize = params.pageSize

  return query
}

function cleanWriteInput(input: Record<string, unknown>): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined) continue
    body[key] = typeof value === 'string' ? value.trim() : value
  }
  return body
}

const emptyByStatus: Record<SalesLeadStatus, number> = {
  new: 0,
  in_progress: 0,
  follow_up: 0,
  seasonal: 0,
  proceed: 0,
  cancelled: 0,
  registered: 0,
}

export const salesLeadService = {
  list: async (
    params: SalesLeadListParams = {},
    config?: AxiosRequestConfig,
  ): Promise<PaginatedSalesLeads> => {
    const payload = await apiGet<ApiListPayload<SalesLeadListItem> & { summary: SalesLeadSummary }>(
      '/sales-leads',
      { ...config, params: toListQuery(params) },
    )
    return {
      data: payload.items ?? [],
      total: payload.total,
      page: payload.page,
      pageSize: payload.pageSize,
      totalPages: payload.totalPages,
      summary: payload.summary ?? {
        total: payload.total,
        byAssignee: [],
        byStatus: emptyByStatus,
        leadCount: 0,
        registeredCount: 0,
        outboundCount: 0,
      },
    }
  },

  /** GET /sales-leads/export — binary XLSX/PDF, same filters as `list` (minus
   * paging) and same ACL (sales_leads.view — no dedicated export permission).
   * `tab` is FE-known UI state passed through purely for the exported file's
   * name/title; see the type's own doc comment. Mirrors the Reports download
   * pattern (`reportService.export`): `apiDownloadBlob` + `triggerBlobDownload`,
   * no separate download stack. */
  export: async (
    params: SalesLeadListParams,
    format: SalesLeadExportFormat,
    tab: SalesLeadExportTab,
    layout?: SalesLeadExportLayout,
  ): Promise<{ filename: string }> => {
    const query = toListQuery(params)
    delete query.page
    delete query.pageSize
    query.format = format
    query.tab = tab
    // XLSX only — the backend ignores this for PDF, so don't bother sending
    // it and pick up a stray param on a lead-level PDF request.
    if (format === 'xlsx' && layout) query.layout = layout
    const { blob, filename } = await apiDownloadBlob('/sales-leads/export', {
      params: query,
      fallbackFilename: `leads-${tab}.${format}`,
      timeout: 120_000,
    })
    triggerBlobDownload(blob, filename)
    return { filename }
  },

  /** Registered Leads tab — dedicated endpoint shape (stage=registered), never the
   * lead-list DTO. */
  listRegistered: async (
    params: Omit<SalesLeadListParams, 'stage'> = {},
    config?: AxiosRequestConfig,
  ): Promise<PaginatedRegisteredLeads> => {
    const payload = await apiGet<ApiListPayload<RegisteredLeadListItem> & { summary: SalesLeadSummary }>(
      '/sales-leads',
      { ...config, params: toListQuery({ ...params, stage: 'registered' }) },
    )
    return {
      data: payload.items ?? [],
      total: payload.total,
      page: payload.page,
      pageSize: payload.pageSize,
      totalPages: payload.totalPages,
      summary: payload.summary ?? {
        total: payload.total,
        byAssignee: [],
        byStatus: emptyByStatus,
        leadCount: 0,
        registeredCount: 0,
        outboundCount: 0,
      },
    }
  },

  assignees: async (config?: AxiosRequestConfig): Promise<SalesLeadUserRef[]> =>
    apiGet<SalesLeadUserRef[]>('/sales-leads/assignees', config),

  getById: async (id: string, config?: AxiosRequestConfig): Promise<SalesLead> =>
    apiGet<SalesLead>(`/sales-leads/${id}`, config),

  create: async (input: SalesLeadCreateInput): Promise<SalesLeadWriteResult> => {
    const { data } = await apiPostResult<SalesLeadWriteResult>(
      '/sales-leads',
      cleanWriteInput({ ...input }),
    )
    return data
  },

  update: async (id: string, input: SalesLeadUpdateInput): Promise<SalesLeadWriteResult> => {
    const { data } = await apiPatchResult<SalesLeadWriteResult>(
      `/sales-leads/${id}`,
      cleanWriteInput({ ...input }),
    )
    return data
  },

  addRemark: async (id: string, body: string): Promise<SalesLead> => {
    const { data } = await apiPostResult<SalesLead>(`/sales-leads/${id}/remarks`, {
      body: body.trim(),
    })
    return data
  },

  addContact: async (id: string, input: SalesLeadContactInput): Promise<SalesLead> => {
    const { data } = await apiPostResult<SalesLead>(
      `/sales-leads/${id}/contacts`,
      cleanWriteInput({ ...input }),
    )
    return data
  },

  updateRemark: async (id: string, remarkId: string, body: string): Promise<SalesLead> => {
    const { data } = await apiPatchResult<SalesLead>(`/sales-leads/${id}/remarks/${remarkId}`, {
      body: body.trim(),
    })
    return data
  },

  deleteRemark: async (id: string, remarkId: string): Promise<SalesLead> => {
    const { data } = await apiDeleteResult<SalesLead>(`/sales-leads/${id}/remarks/${remarkId}`)
    return data
  },

  updateContact: async (
    id: string,
    contactId: string,
    input: SalesLeadContactInput,
  ): Promise<SalesLead> => {
    const { data } = await apiPatchResult<SalesLead>(
      `/sales-leads/${id}/contacts/${contactId}`,
      cleanWriteInput({ ...input }),
    )
    return data
  },

  deleteContact: async (id: string, contactId: string): Promise<SalesLead> => {
    const { data } = await apiDeleteResult<SalesLead>(`/sales-leads/${id}/contacts/${contactId}`)
    return data
  },

  register: async (id: string, input: SalesLeadRegisterInput): Promise<SalesLeadWriteResult> => {
    const { data } = await apiPostResult<SalesLeadWriteResult>(
      `/sales-leads/${id}/register`,
      cleanWriteInput({ ...input }),
    )
    return data
  },

  updateRegistered: async (
    id: string,
    input: SalesLeadUpdateRegisteredInput,
  ): Promise<SalesLeadWriteResult> => {
    const { data } = await apiPatchResult<SalesLeadWriteResult>(
      `/sales-leads/${id}/registered`,
      cleanWriteInput({ ...input }),
    )
    return data
  },

  /** Deal Done — on the registered record only; requires a RegisteredLead to exist. */
  close: async (id: string, input: SalesLeadCloseInput): Promise<SalesLead> => {
    const { data } = await apiPostResult<SalesLead>(
      `/sales-leads/${id}/close`,
      cleanWriteInput({ ...input }),
    )
    return data
  },

  /** quotationId=null unselects — a single endpoint covers both. */
  selectQuotation: async (id: string, quotationId: string | null): Promise<SalesLead> => {
    const { data } = await apiPostResult<SalesLead>(`/sales-leads/${id}/select-quotation`, {
      quotationId,
    })
    return data
  },

  delete: async (id: string): Promise<void> => {
    await apiDelete(`/sales-leads/${id}`)
  },
}
